import React, { useMemo, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, DualAxes } from '@ant-design/plots';
import { Button, Typography, Spin, Flex, ConfigProvider, Collapse } from '@app/ui';
import { DownloadOutlined } from '@ant-design/icons';
import { SocialBenchmarkingContext } from './SocialBenchmarkingContext';
import { longDate } from '@app/utils/formatters';
import {
    ENGAGEMENT_METRIC,
    FOLLOWER_METRIC,
    POST_METRIC,
} from '@app/constants/sbMetrics';
import {
    platformsConfig,
    mediaTypeConfig,
} from '@app/constants/visualizationConfig';
import {
    platformOptions,
    selectedOptionsToString,
    selectedOptionsToArray,
} from './socialbenchmarking-filters';
import { isSameYear } from 'date-fns';
import useDebounce from '@app/components/hooks/useDebounce';
import * as format from '@app/utils/formatters';

const { Text } = Typography;

interface SocialBenchmarkingChartProps {
    socialBenchmarking: {
        accountGroups: any[];
        favoritesList: any[];
        isLoading: boolean;
        leagues: any[];
        periodStartDate: string;
        periodEndDate: string;
        hasNoData: boolean;
    };
    sendNotifications: (message: string, options: any) => void;
}

const SocialBenchmarkingChart: React.FC<SocialBenchmarkingChartProps> = ({
    socialBenchmarking,
    sendNotifications,
}) => {
    const { t } = useTranslation('benchmarking');
    const [isExpanded, setIsExpanded] = useState(true);
    const chartRef = useRef<any>(null);
    const collapseContentRef = useRef<HTMLDivElement>(null);
    const debouncedHeight = useDebounce(
        collapseContentRef.current?.clientHeight,
        100,
    );

    const isCollapseFullyOpen =
        collapseContentRef.current?.clientHeight === debouncedHeight &&
        collapseContentRef.current?.clientHeight > 0;

    const {
        accountGroups,
        isLoading,
        periodStartDate,
        periodEndDate,
        hasNoData,
        favoritesList,
        leagues,
    } = socialBenchmarking;

    const {
        socialMetric,
        currentSocialMetricFilterValue,
        periodicity,
        platformsValues,
        mediaTypesValues,
        currentVisualizationValue,
        selectedHeader,
        isFollowerMetricCurrent,
        mediaOptions,
        leagueFilterValue,
        currentPageData,
    } = useContext(SocialBenchmarkingContext);

    const title = useMemo(() => {
        if (!accountGroups.length) return null;
        if (!leagues && !favoritesList) return null;
        const showLeagues = !Number.isNaN(parseInt(leagueFilterValue));
        return showLeagues
            ? t('socialBenchmarking.chart.title.league', {
                  league:
                      leagues.find(l => l.id == leagueFilterValue)?.name || '',
                  metric: socialMetric,
              })
            : t('socialBenchmarking.chart.title.favorites', {
                  metric: socialMetric,
              });
    }, [
        socialMetric,
        accountGroups,
        leagues,
        favoritesList,
        currentPageData,
        leagueFilterValue,
        t,
    ]);

    // English title for PNG filename (always in English)
    const englishTitleForFilename = useMemo(() => {
        if (!accountGroups.length) return null;
        if (!leagues && !favoritesList) return null;
        const showLeagues = !Number.isNaN(parseInt(leagueFilterValue));
        if (showLeagues) {
            const leagueName =
                leagues.find(l => l.id == leagueFilterValue)?.name ||
                leagueFilterValue ||
                '';
            return `${leagueName} Account Groups by ${socialMetric}`;
        } else {
            return `Favorite Account Groups by ${socialMetric}`;
        }
    }, [
        socialMetric,
        accountGroups,
        leagues,
        favoritesList,
        leagueFilterValue,
    ]);

    const handleDownload = e => {
        try {
            e.stopPropagation();
            e.preventDefault();
            const downloadTitle = englishTitleForFilename
                ? englishTitleForFilename.replace(/\s/g, '_')
                : 'chart';
            const chart = chartRef.current;
            if (chart) {
                chart?.downloadImage(`${downloadTitle}.png`);
                sendNotifications(
                    t('socialBenchmarking.chart.export.success'),
                    {
                        context: 'success',
                    },
                );
            } else {
                sendNotifications(
                    t('socialBenchmarking.chart.export.noChart'),
                    {
                        context: 'info',
                    },
                );
            }
        } catch (e) {
            sendNotifications(t('socialBenchmarking.chart.export.error'), {
                context: 'error',
            });
        }
    };

    const titleContent = (
        <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            <Flex
                gap={12}
                justify="flex-start"
                align="center"
                style={{
                    height: 'fit-content',
                    fontSize: 20,
                }}
            >
                {title}
            </Flex>
            <Button
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                type="primary"
                color="primary"
                variant="solid"
            >
                {t('socialBenchmarking.chart.export.downloadPng')}
            </Button>
        </Flex>
    );

    // Get current page account groups from context
    const ags = useMemo(() => {
        return currentPageData.length > 0
            ? currentPageData
            : accountGroups
                  .sort((a, b) => b[selectedHeader] - a[selectedHeader])
                  .slice(0, 10);
    }, [currentPageData, accountGroups, selectedHeader]);

    const selectedVisualization = currentVisualizationValue;

    const selectedPlatforms = selectedOptionsToString(
        platformsValues,
        platformOptions,
    );
    const selectedMediaTypes = mediaOptions
        ? selectedOptionsToString(mediaTypesValues, mediaOptions)
        : null;

    const selectedDates = useMemo(() => {
        if (!periodStartDate || !periodEndDate) {
            return {
                startDate: '',
                endDate: '',
            };
        }

        const startDate = new Date(periodStartDate.replace(/-/g, '/'));
        const endDate = new Date(periodEndDate.replace(/-/g, '/'));

        return {
            startDate: isSameYear(startDate, endDate)
                ? longDate(startDate, false)
                : longDate(startDate, true),
            endDate: longDate(endDate, true),
        };
    }, [accountGroups, periodStartDate, periodEndDate]);

    // Prepare chart data based on visualization type
    const chartData = useMemo(() => {
        if (
            selectedVisualization === 'platform' ||
            selectedVisualization === 'mediaType'
        ) {
            // Stacked column chart
            let config = null;
            let selectedOptions = null;

            if (selectedVisualization === 'platform') {
                config = platformsConfig;
                selectedOptions = selectedOptionsToArray(
                    platformsValues,
                    platformOptions,
                );
            } else {
                config = mediaTypeConfig;
                selectedOptions = selectedOptionsToArray(
                    mediaTypesValues,
                    mediaOptions,
                );
            }

            const data: any[] = [];

            ags.forEach(ag => {
                selectedOptions.forEach(op => {
                    const label = config.find(
                        option => option.value == op,
                    )?.label;
                    const found =
                        ag.partitions &&
                        ag.partitions.filter(p => p.partitionId == op);
                    const value = found && found.length ? found[0].value : 0;

                    data.push({
                        accountGroup: ag.accountGroupName,
                        category: label,
                        value: value,
                    });
                });
            });

            return { type: 'stacked', data };
        } else {
            // Dual axes chart (bar + line)
            let countPerDayHeader = null;
            let countPerDayAccessor = null;

            switch (currentSocialMetricFilterValue) {
                case POST_METRIC:
                    countPerDayAccessor = 'postVolumeChange';
                    countPerDayHeader = t(
                        'socialBenchmarking.chart.periodicityChange',
                        { periodicity },
                    );
                    break;
                case ENGAGEMENT_METRIC:
                    countPerDayAccessor = 'engagementsPerPost';
                    countPerDayHeader = t(
                        'socialBenchmarking.chart.metricPerPost',
                        { metric: socialMetric },
                    );
                    break;
                case FOLLOWER_METRIC:
                    countPerDayAccessor = 'newFollowers';
                    countPerDayHeader = t(
                        'socialBenchmarking.chart.changeInFollowers',
                    );
                    break;
                default:
                    break;
            }

            // SC-2068: Allow line graph with engagement rate data
            if (selectedVisualization === 'engagementRate') {
                countPerDayAccessor = 'engagementRate';
                countPerDayHeader = t(
                    'socialBenchmarking.chart.engagementRate',
                );
            }

            const barData = ags.map(ag => ({
                accountGroup: ag.accountGroupName,
                value: ag.itemCount,
                type: socialMetric,
            }));

            const lineData = ags.map(ag => ({
                accountGroup: ag.accountGroupName,
                value: Math.round(ag[countPerDayAccessor] * 10) / 10,
                type: countPerDayHeader,
            }));

            return { type: 'dual', barData, lineData };
        }
    }, [
        ags,
        socialMetric,
        selectedVisualization,
        currentSocialMetricFilterValue,
        periodicity,
        isCollapseFullyOpen,
        t,
    ]);

    const renderChart = () => {
        if (chartData.type === 'stacked') {
            // Function to create annotations based on data
            const createAnnotations = data => {
                const annotations = [];

                // Group data by accountGroup
                const groupedData = data.reduce(
                    (acc, item) => {
                        if (!acc[item.accountGroup]) {
                            acc[item.accountGroup] = [];
                        }
                        acc[item.accountGroup].push(item);
                        return acc;
                    },
                    {} as Record<string, any[]>,
                );

                // Create annotations for each account group
                Object.entries(groupedData).forEach(
                    ([accountGroup, values]) => {
                        const totalValue = (values as any[]).reduce(
                            (sum, item) => sum + item.value,
                            0,
                        );
                        annotations.push({
                            type: 'text',
                            data: [accountGroup, totalValue],
                            xField: 'accountGroup',
                            yField: 'value',
                            style: {
                                text: `${format.abbreviatedDecimal(totalValue)}`,
                                textBaseline: 'bottom',
                                position: 'top',
                                textAlign: 'center',
                                fontSize: 14,
                                fill: 'rgba(0,0,0,0.85)',
                            },
                            tooltip: false,
                        });
                    },
                );

                return annotations;
            };

            // Create initial annotations
            const annotations = createAnnotations(chartData.data);
            // Stacked column chart
            const config = {
                data: chartData.data,
                xField: 'accountGroup',
                yField: 'value',
                colorField: 'category',
                stack: true,
                height: 530,
                annotations,
                legend: {
                    color: {
                        position: 'top',
                        layout: {
                            justifyContent: 'flex-start',
                        },
                    },
                },
                tooltip: {
                    shared: true,
                    field: 'value',
                    valueFormatter: format.abbreviatedDecimal,
                },
                axis: {
                    y: {
                        title: socialMetric,
                        grid: true,
                        line: true,
                        tickLine: true,
                        labelFormatter: value =>
                            format.abbreviatedDecimal(value),
                    },
                },
                theme: {
                    view: {
                        viewFill: '#ffffff',
                        plotFill: '#ffffff',
                    },
                },
            };

            return (
                <Column
                    {...config}
                    onReady={chart => (chartRef.current = chart)}
                />
            );
        } else {
            // Dual axes chart (bar + line)
            const { barData, lineData } = chartData;

            // Combine data for dual axes
            const combinedData = barData.map((barItem, index) => {
                const lineItem = lineData[index];
                const barFieldName = barItem.type;
                const lineFieldName = lineItem?.type || 'lineValue';

                return {
                    accountGroup: barItem.accountGroup,
                    [barFieldName]: barItem.value,
                    [lineFieldName]: lineItem?.value || 0,
                };
            });

            const barFieldName = barData[0]?.type || 'barValue';
            const lineFieldName = lineData[0]?.type || 'lineValue';

            const config = {
                data: combinedData,
                xField: 'accountGroup',
                height: 530,
                legend: {
                    color: {
                        position: 'top',
                        layout: {
                            justifyContent: 'flex-start',
                        },
                        itemSpacing: [5, 5],
                    },
                },
                scale: {
                    color: {
                        range: ['#36a2eb', '#ff6384'],
                    },
                },
                theme: {
                    view: {
                        viewFill: '#ffffff',
                        plotFill: '#ffffff',
                    },
                },
                tooltip: {
                    channel: 'y',
                    valueFormatter: format.abbreviatedDecimal,
                },
                children: [
                    {
                        type: 'interval',
                        yField: barFieldName,
                        style: { maxWidth: 80 },
                        axis: {
                            y: {
                                title: socialMetric,
                                titleFill: '#36a2eb',
                                position: 'left',
                                labelFill: '#36a2eb',
                                labelFormatter: value =>
                                    format.abbreviatedDecimal(value),
                            },
                        },
                    },
                    {
                        type: 'line',
                        yField: lineFieldName,
                        style: { lineWidth: 2 },
                        axis: {
                            y: {
                                title: t(
                                    'socialBenchmarking.chart.engagementRate',
                                ),
                                titleFill: '#ff6384',
                                titleTransform: 'rotate(90)',
                                position: 'right',
                                labelFill: '#ff6384',
                                labelFormatter: value =>
                                    format.abbreviatedDecimal(value),
                            },
                        },
                    },
                ],
            };

            return (
                <DualAxes
                    {...config}
                    onReady={chart => (chartRef.current = chart)}
                />
            );
        }
    };

    const shouldShowGraph = (isLoading || ags.length > 0) && !hasNoData;
    const shouldShowTitle = !isLoading && ags.length > 0 && !hasNoData;

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerBg: '#FFF',
                        colorBorder: '#BFBFBF',
                    },
                },
            }}
        >
            <Collapse
                style={{
                    top: 0,
                    overflow: 'hidden',
                    marginTop: '24px',
                }}
                activeKey={isExpanded ? ['1'] : []}
                onChange={() => setIsExpanded(!isExpanded)}
                expandIconPosition="end"
                items={[
                    {
                        key: '1',
                        label: titleContent,
                        children: (
                            <Flex gap={16} ref={collapseContentRef} vertical>
                                {shouldShowTitle && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <div>
                                            <Text
                                                type="secondary"
                                                style={{ fontSize: '14px' }}
                                            >
                                                {selectedDates.startDate} -{' '}
                                                {selectedDates.endDate} •{' '}
                                                {selectedPlatforms}
                                                {!isFollowerMetricCurrent &&
                                                    ` • ${selectedMediaTypes}`}
                                            </Text>
                                        </div>
                                    </div>
                                )}
                                {shouldShowGraph && (
                                    <div
                                        style={{
                                            flex: 1,
                                        }}
                                    >
                                        <Spin spinning={isLoading}>
                                            {renderChart()}
                                        </Spin>
                                    </div>
                                )}
                                {!shouldShowGraph && (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            padding: '32px',
                                            color: '#999',
                                        }}
                                    >
                                        {t('socialBenchmarking.chart.noData')}
                                    </div>
                                )}
                            </Flex>
                        ),
                    },
                ]}
            />
        </ConfigProvider>
    );
};

export default SocialBenchmarkingChart;
